import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';

const AboutPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              About SKYCODE
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Philippines-based software development company turning ideas into digital reality
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
              <p className="text-gray-600 mb-4">
                Founded in 2016, SKYCODE began as a small team of passionate developers 
                dedicated to helping businesses leverage technology for growth. What started 
                as a freelance operation has grown into a full-service software development 
                company serving clients across the Philippines and beyond.
              </p>
              <p className="text-gray-600 mb-4">
                We've evolved from building simple websites to developing complex e-commerce 
                platforms, MLM systems, blockchain applications, and custom enterprise solutions. 
                Our journey has been driven by one core principle: deliver solutions that 
                create real business value.
              </p>
              <p className="text-gray-600">
                Today, we're proud to be a trusted technology partner for businesses ranging 
                from startups to established enterprises, helping them navigate the digital 
                landscape with confidence and success.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-8 flex items-center justify-center">
              <div className="text-center">
                <div className="text-6xl font-bold text-gray-900 mb-4">4+</div>
                <div className="text-xl text-gray-700">Years of Innovation</div>
              </div>
            </div>
          </div>

          <div className="mb-20">
            <h2 className="text-3xl font-bold text-black mb-8 text-center">Our Vision & Mission</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-black mb-4">Our Vision</h3>
                <p className="text-gray-600">
                  To be the leading technology partner in the Philippines, empowering businesses 
                  with innovative digital solutions that drive sustainable growth and competitive advantage.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-black mb-4">Our Mission</h3>
                <p className="text-gray-600">
                  To deliver exceptional software solutions that solve real business problems, 
                  combining technical expertise with deep understanding of our clients' needs and goals.
                </p>
              </div>
            </div>
          </div>

          <div className="mb-20">
            <h2 className="text-3xl font-bold text-black mb-8 text-center">Core Values</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-center">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-black mb-2">Quality</h3>
                <p className="text-gray-600 text-sm">Excellence in every line of code and every client interaction</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-center">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-black mb-2">Timely Delivery</h3>
                <p className="text-gray-600 text-sm">Respecting deadlines and communicating proactively</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-center">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-black mb-2">Client-Centric</h3>
                <p className="text-gray-600 text-sm">Your success is our success and primary focus</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-center">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-black mb-2">Innovation</h3>
                <p className="text-gray-600 text-sm">Embracing new technologies and creative solutions</p>
              </div>
            </div>
          </div>

          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Expertise</h2>
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Web Development</h3>
                  <ul className="text-gray-600 space-y-1 text-sm">
                    <li>• React & Next.js Applications</li>
                    <li>• E-commerce Platforms</li>
                    <li>• Progressive Web Apps</li>
                    <li>• API Development</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Mobile Development</h3>
                  <ul className="text-gray-600 space-y-1 text-sm">
                    <li>• React Native Apps</li>
                    <li>• iOS & Android Development</li>
                    <li>• Cross-Platform Solutions</li>
                    <li>• App Store Optimization</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Specialized Systems</h3>
                  <ul className="text-gray-600 space-y-1 text-sm">
                    <li>• MLM Platforms</li>
                    <li>• Blockchain Solutions</li>
                    <li>• Custom ERP Systems</li>
                    <li>• Cybersecurity</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center bg-gray-900 text-white p-8 rounded-lg">
            <h2 className="text-2xl font-bold mb-4">Ready to Work Together?</h2>
            <p className="mb-6 max-w-2xl mx-auto">
              Let's discuss how we can help bring your digital vision to life with our expertise 
              and commitment to excellence.
            </p>
            <Link
              to="/contact"
              className="bg-gray-900 text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
            >
              Start Your Project
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
